import React, { useState } from 'react'
import {
  Tr,
  Td,
  Badge,
  IconButton,
  HStack,
  Tooltip,
  Link,
  Icon,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  FormErrorMessage,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Flex,
} from '@chakra-ui/react'
import { DeleteIcon, StarIcon, ExternalLinkIcon, CheckIcon, WarningIcon, EditIcon } from '@chakra-ui/icons'
import { MdCameraAlt, MdEdit, MdComment } from 'react-icons/md'
import { format, isValid, parseISO } from 'date-fns'
import { useUser } from '../../../../hooks/useUser'
import ModalUploadFotos from './ModalUploadFotos'

function Item({ produto, onAlterarPrioridade, onAbrirModalEditar, onAdicionarFoto, onDeletarProduto, onEditarObservacao }) {
  const { isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure()
  const { isOpen: isOpenFoto, onOpen: onOpenFoto, onClose: onCloseFoto } = useDisclosure()
  const { isOpen: isOpenObs, onOpen: onOpenObs, onClose: onCloseObs } = useDisclosure()

  // Estado para armazenar a descrição da exclusão
  const [descricaoExclusao, setDescricaoExclusao] = useState('')
  // Estado para controlar erro de validação
  const [descricaoError, setDescricaoError] = useState(false)
  // Estado para armazenar a observação do produto
  const [observacao, setObservacao] = useState(produto.obs || '')
  // Estado para indicar que está salvando a observação
  const [salvandoObs, setSalvandoObs] = useState(false)

  const cancelRef = React.useRef()
  const { codigo_usuario } = useUser()

  // Função para resetar o formulário ao fechar o modal
  const handleCloseDelete = () => {
    setDescricaoExclusao('')
    setDescricaoError(false)
    onCloseDelete()
  }

  // Função para abrir o modal de observação e inicializar com o valor atual
  const handleOpenObsModal = () => {
    setObservacao(produto.obs || '')
    onOpenObs()
  }

  // Função para salvar a observação
  const handleSalvarObservacao = async () => {
    try {
      setSalvandoObs(true)
      await onEditarObservacao(produto.id, observacao)
      onCloseObs()
    } catch (error) {
      console.error('Erro ao salvar observação:', error)
    } finally {
      setSalvandoObs(false)
    }
  }

  const verificarStatusLink = () => {
    return produto.link_produto
      ? { status: 'concluído', icon: <CheckIcon color="green.500" />, label: 'Link adicionado' }
      : { status: 'pendente', icon: <WarningIcon color="red.500" />, label: 'Link pendente' }
  }

  const verificarStatusFoto = () => {
    return produto.data_foto
      ? { status: 'concluído', icon: <CheckIcon color="green.500" />, label: `Foto enviada: ${formatarDataAPI(produto.data_foto)}` }
      : { status: 'pendente', icon: <WarningIcon color="orange.500" />, label: 'Foto pendente' }
  }

  const verificarStatusCadastro = () => {
    return produto.data_cadastro
      ? { status: 'concluído', icon: <CheckIcon color="green.500" />, label: `Data cadastrada: ${formatarDataAPI(produto.data_cadastro)}` }
      : { status: 'pendente', icon: <WarningIcon color="yellow.500" />, label: 'Cadastro pendente' }
  }

  const handleAdicionarFoto = (formData) => {
    return onAdicionarFoto(formData)
  }

  const formatarDataAPI = (dataString) => {
    if (!dataString) return '-'

    try {
      const data = parseISO(dataString)
      return isValid(data) ? format(data, 'dd/MM/yyyy HH:mm') : '-'
    } catch (error) {
      return '-'
    }
  }

  const statusLink = verificarStatusLink()
  const statusFoto = verificarStatusFoto()
  const statusCadastro = verificarStatusCadastro()

  // Modificada para validar a descrição antes de excluir
  const handleConfirmarExclusao = () => {
    if (!descricaoExclusao.trim()) {
      setDescricaoError(true)
      return
    }

    onDeletarProduto(produto.id, codigo_usuario, descricaoExclusao)
    handleCloseDelete()
  }

  return (
    <>
      <Tr>
        <Td>{produto.ref_fornecedor}</Td>
        <Td maxW="120px" isTruncated title={produto.produto}>
          <Tooltip label={produto.produto}>{produto.produto || '-'}</Tooltip>
        </Td>
        <Td>{produto.fornecedor}</Td>
        <Td>{produto.cod_inno || '-'}</Td>
        <Td isNumeric>{produto.estoque}</Td>
        <Td>{formatarDataAPI(produto.data_api)}</Td>
        <Td>
          {produto.link_produto ? (
            <Tooltip label={produto.link_produto}>
              <Link href={produto.link_produto} isExternal color="blue.500">
                <Icon as={ExternalLinkIcon} />
              </Link>
            </Tooltip>
          ) : (
            '-'
          )}
        </Td>

        {/* Coluna Status Link */}
        <Td>
          <Tooltip label={statusLink.label}>{statusLink.icon}</Tooltip>
        </Td>

        {/* Coluna Status Foto */}
        <Td>
          {statusFoto.status === 'pendente' ? (
            <Tooltip label="Adicionar fotos ao produto">
              <IconButton
                isDisabled={!produto.cod_inno}
                aria-label="Adicionar fotos"
                icon={<Icon as={MdCameraAlt} />}
                size="xs"
                colorScheme="orange"
                onClick={onOpenFoto}
              />
            </Tooltip>
          ) : (
            <Tooltip label={statusFoto.label}>{produto.data_foto}</Tooltip>
          )}
        </Td>

        {/* Coluna Status Cadastro */}
        <Td>
          {statusCadastro.status === 'pendente' ? (
            <Tooltip label="Completar cadastro do produto">
              <IconButton
                aria-label="Editar dados"
                icon={<Icon as={MdEdit} />}
                size="xs"
                colorScheme="yellow"
                onClick={() => onAbrirModalEditar(produto.id)}
              />
            </Tooltip>
          ) : (
            <Tooltip label={statusCadastro.label}>{produto.data_cadastro}</Tooltip>
          )}
        </Td>

        <Td>
          <Badge colorScheme={produto.prioridade === 'S' ? 'purple' : 'gray'}>{produto.prioridade === 'S' ? 'Prioritário' : 'Normal'}</Badge>
        </Td>
        <Td>
          <Flex alignItems="center">
            <Tooltip label={produto.obs || 'Sem observações'}>
              <Text maxW="150px" isTruncated color={produto.obs ? 'black' : 'gray.500'} mr={2}>
                {produto.obs || 'Sem observações'}
              </Text>
            </Tooltip>
            <IconButton aria-label="Editar observação" icon={<Icon as={MdComment} />} size="xs" colorScheme="teal" onClick={handleOpenObsModal} />
          </Flex>
        </Td>
        <Td>
          <HStack spacing={2}>
            <Tooltip label={produto.prioridade === 'S' ? 'Remover prioridade' : 'Marcar como prioritário'} placement="top">
              <IconButton
                aria-label="Alterar prioridade"
                icon={<StarIcon />}
                size="sm"
                colorScheme={produto.prioridade === 'S' ? 'yellow' : 'gray'}
                onClick={() => onAlterarPrioridade(produto.id)}
              />
            </Tooltip>

            <Tooltip label="Excluir produto" placement="top">
              <IconButton aria-label="Excluir produto" icon={<DeleteIcon />} size="sm" colorScheme="red" onClick={onOpenDelete} />
            </Tooltip>
          </HStack>
        </Td>
      </Tr>

      {/* Modal de confirmação de exclusão */}
      <AlertDialog isOpen={isOpenDelete} leastDestructiveRef={cancelRef} onClose={handleCloseDelete}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Excluir Produto
            </AlertDialogHeader>

            <AlertDialogBody>
              <Text mb={4}>Tem certeza que deseja excluir o produto {produto.ref_fornecedor}? Esta ação não pode ser desfeita.</Text>

              <FormControl isInvalid={descricaoError} isRequired>
                <FormLabel>Motivo da exclusão</FormLabel>
                <Textarea
                  value={descricaoExclusao}
                  onChange={(e) => {
                    setDescricaoExclusao(e.target.value)
                    if (e.target.value.trim()) setDescricaoError(false)
                  }}
                  placeholder="Descreva o motivo da exclusão do produto"
                  size="sm"
                />
                {descricaoError && <FormErrorMessage>É necessário informar o motivo da exclusão</FormErrorMessage>}
              </FormControl>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={handleCloseDelete}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleConfirmarExclusao} ml={3} isDisabled={!descricaoExclusao.trim()}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Modal de upload de fotos */}
      <ModalUploadFotos
        isOpen={isOpenFoto}
        onClose={onCloseFoto}
        produto={{
          ...produto,
          id_usuario: codigo_usuario,
        }}
        onEnviarFotos={handleAdicionarFoto}
        isLoading={false}
      />

      {/* Modal de edição de observação */}
      <Modal isOpen={isOpenObs} onClose={onCloseObs}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar Observação</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Observação sobre o produto {produto.ref_fornecedor}</FormLabel>
              <Textarea
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Digite uma observação sobre o produto"
                rows={4}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCloseObs}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={handleSalvarObservacao} isLoading={salvandoObs} loadingText="Salvando...">
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default Item
